import '../../styles/Details.scss';

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { auth } from '../../firebase-config';
import { MovieModel } from "../../models";
import { setBackgroundImage } from '../../helpers/helpers';

import InfoList from "../../components/InfoList";
import Loader from '../../components/Loader';
import PersonList from '../../components/PersonList';
import Ratings from '../../components/Ratings';
import WatchPopup from '../../components/WatchPopup';
import { useHistory } from '../../hooks/HistoryContext';

const MovieDetails = () => {
    const { id } = useParams();
    const { history } = useHistory();
    const [watched, setWatched] = useState(false);
    const [historyButtonText, setHistoryButtonText] = useState('Add to History');

    const getMovieDetails = async (id: string | undefined) => {
        const apiKey = process.env.REACT_APP_TMDB_API_KEY;
        const response = await fetch(`https://api.themoviedb.org/3/movie/${id}?append_to_response=credits,production_companies&language=en-GB`, {
            headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${apiKey}`,
            }
        });
        return await response.json();
    }
    
    const { data: movieDetails, isLoading, error } = useQuery<MovieModel>({
        queryKey: ['movies', id],
        queryFn: () => getMovieDetails(id),
        enabled: !!id, // Only run query if id exists
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    useEffect(() => {
        setWatched(history.some(item => item.tmdb_id.toString() === movieDetails?.id.toString()));
        setHistoryButtonText(watched ? 'Watch Again' : 'Add to History');
    }, [history, movieDetails, watched]);
    
    if (isLoading || !movieDetails) return <Loader fullscreen message="Loading..." />;
    if (error) return <div>Error loading movie details.</div>;

    setBackgroundImage(movieDetails.backdrop_path);
    movieDetails.media_type = 'movie';
    const pageTitle = `${movieDetails.title} (${new Date(movieDetails.release_date).getFullYear()})`;
    
    const infoItems = [
        { label: 'Release Date', value: movieDetails.release_date ? format(new Date(movieDetails.release_date), 'd MMMM yyyy') : null },
        { label: 'Runtime', value: movieDetails.runtime > 0 ? `${movieDetails.runtime} min` : null },
        { 
            label: 'Director',
            value: movieDetails.credits?.crew.filter(person => person.job === 'Director').map(director => ({
                id: director.id,
                name: director.name,
            })) || []
        },
        {
            label: 'Writer',
            value: movieDetails.credits?.crew.filter(person => person.job === 'Writer').map(writer => ({
                id: writer.id,
                name: writer.name,
            })) || []
        },
        { label: 'Studio', value: movieDetails.production_companies.map(c => c.name)?.join(', ') },
        { label: 'Budget', value: movieDetails.budget ? `$${movieDetails.budget.toLocaleString()}` : null },
        { label: 'Revenue', value: movieDetails.revenue ? `$${movieDetails.revenue.toLocaleString()}` : null },
        { label: 'Genre', value: movieDetails.genres.map(genre => genre.name).join(', ') },
    ];

    document.title = pageTitle;
    
    return (
        <>
            <div>

                <div className="details-summary-container">
                    <div className="details-poster">
                        <img src={`https://image.tmdb.org/t/p/w500${movieDetails.poster_path}`} alt={movieDetails.title} />
                        {auth.currentUser && (
                            <>
                                <button className="history-button">{historyButtonText}</button>
                                <WatchPopup anchorSelect=".history-button" media={movieDetails} onWatched={() => {setHistoryButtonText('Watch Again')}} />
                            </>
                        )}
                    </div>

                    <div className="details-summary-content">
                        <div className="content-children">
                            <div className="title-container">
                                <h1 className="details-title">{movieDetails.title}</h1>

                                <Ratings details={movieDetails} link={`movie/${movieDetails.id}`} />
                            </div>

                        </div>

                        <p className="details-overview">{movieDetails.overview}</p>
                    </div>
                </div>

                <div className="details-summary-container">
                    <div className="details-summary-content">
                        <InfoList items={infoItems} />
                    </div>
                </div>
            </div>
            
            <div className="details-person-container">
                <PersonList people={movieDetails.credits?.cast || []} />
            </div>
        </>
    );
};

export default MovieDetails;
